import React, { useState, useEffect } from "react";
import { Card, Button, Modal, Form, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import { FiPlus, FiEdit2, FiTrash2, FiFileText } from "react-icons/fi";
import { showToast } from "../../../components/common/notification/Notification";

const LeaseNotes = ({ leaseId }) => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add Note Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [adding, setAdding] = useState(false);

  // Edit Note Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [editing, setEditing] = useState(false);

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchNotes = async () => {
    if (!leaseId) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/lease/notes`,
        {
          lead_id: Number(leaseId),
          lease_id: Number(leaseId),
          id: Number(leaseId),
          page_number: 1,
          page_size: 50,
          order_by: "ASC",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resData = response.data;
      const list =
        resData?.notes ||
        resData?.leaseNotes ||
        resData?.lease_notes ||
        resData?.data ||
        resData?.content ||
        (Array.isArray(resData) ? resData : []);

      setNotes(list);
    } catch (error) {
      console.error("Error fetching lease notes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId, baseUrl, token]);

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showToast("Please enter note content", { type: "error" });
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(
        `${baseUrl}/lease/note/add`,
        {
          lease: {
            id: Number(leaseId),
          },
          note: newNote,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Note added successfully!", { type: "success" });
        setNewNote("");
        setShowAddModal(false);
        fetchNotes();
      } else {
        showToast(response.data?.text || "Failed to add note", { type: "error" });
      }
    } catch (error) {
      console.error("Error adding lease note:", error);
      showToast("Error adding note", { type: "error" });
    } finally {
      setAdding(false);
    }
  };

  const handleEditNote = async () => {
    if (!editedContent.trim() || !editingNote) return;

    setEditing(true);
    try {
      const response = await axios.put(
        `${baseUrl}/lease/note/edit`,
        {
          id: Number(editingNote.id),
          note: editedContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Note updated successfully!", { type: "success" });
        setShowEditModal(false);
        setEditingNote(null);
        setEditedContent("");
        fetchNotes();
      } else {
        showToast(response.data?.text || "Failed to update note", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating lease note:", error);
      showToast("Error updating note", { type: "error" });
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!deletingId) return;

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${baseUrl}/lease/note/delete/${deletingId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Note deleted successfully!", { type: "success" });
        setNotes((prev) => prev.filter((n) => n.id !== deletingId));
        setShowDeleteModal(false);
        setDeletingId(null);
      } else {
        showToast(response.data?.text || "Failed to delete note", { type: "error" });
      }
    } catch (error) {
      console.error("Error deleting lease note:", error);
      showToast("Error deleting note", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="shadow-sm border rounded-3 mt-4">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
        <h5 className="mb-0 fw-semibold d-flex align-items-center">
          <FiFileText className="me-2 text-primary" /> Lease Notes
        </h5>
        <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
          <FiPlus className="me-1" /> Add Note
        </Button>
      </Card.Header>

      <Card.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-muted text-center mb-0 py-2">No notes added for this lease yet.</p>
        ) : (
          <div className="d-flex flex-column gap-3">
            {notes.map((item) => (
              <div
                key={item.id}
                className="p-3 border rounded bg-light d-flex justify-content-between align-items-start"
              >
                <div className="flex-grow-1 me-3">
                  <p className="mb-1 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {item.note || item.content || item.description}
                  </p>
                  {item.created_on && (
                    <small className="text-muted">
                      {new Date(item.created_on).toLocaleString()}
                    </small>
                  )}
                </div>

                <div className="d-flex gap-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="p-1 px-2"
                    title="Edit Note"
                    onClick={() => {
                      setEditingNote(item);
                      setEditedContent(item.note || item.content || "");
                      setShowEditModal(true);
                    }}
                  >
                    <FiEdit2 size={14} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="p-1 px-2"
                    title="Delete Note"
                    onClick={() => {
                      setDeletingId(item.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    <FiTrash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>

      {/* Add Note Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Add Lease Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="addNoteContent">
            <Form.Label className="fw-semibold">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter note details..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowAddModal(false)} disabled={adding}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleAddNote} disabled={adding}>
            {adding ? "Saving..." : "Add Note"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Note Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Edit Lease Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="editNoteContent">
            <Form.Label className="fw-semibold">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              placeholder="Enter updated note details..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEditModal(false)} disabled={editing}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleEditNote} disabled={editing}>
            {editing ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this lease note?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteNote} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default LeaseNotes;
